import { DataGridPremium, GridColDef } from '@mui/x-data-grid-premium';
import { mgPrisEnvConfigs } from '@retail/mgpris/config';
import {
  FeatureFlag,
  FeatureScope,
  MgPrisFeature,
  mgPrisFeatures,
} from '@retail/mgpris/config-types';
import { Chip } from '@shared/components';
import { Stack, Tooltip, Typography, useTheme } from '@mui/material';
import { useRowColorGrouping } from '@retail/hooks';

const devFeatures = mgPrisEnvConfigs.dev.featureFlags;
const stageFeatures = mgPrisEnvConfigs.stage.featureFlags;
const prodFeatures = mgPrisEnvConfigs.prod.featureFlags;

const features = mgPrisFeatures.map((feature) => ({
  name: feature,
  envs: {
    dev: devFeatures.find(({ name }) => name === feature),
    stage: stageFeatures.find(({ name }) => name === feature),
    prod: prodFeatures.find(({ name }) => name === feature),
  },
}));

type RowType = (typeof features)[number];

const descriptionMap: Record<MgPrisFeature, string> = {
  'help-center': 'Shows help center in header',
  'supplier-price-change-notification-config': 'Enable supplier price change notifications',
  'sales-price-condition': 'Show "Salespricecondition" in calculus',
  local_campaigns: 'Create local campaigns',
  chatbot: 'Show chatbot',
  'price-rounding': 'Show price rounding',
  'price-control': 'Show price control',
  'all-conditions': 'Show "All conditions" tab in calculus',
  'local-price-suggestions': 'Show local price suggestions',
  'new-dashboard': 'Dashboard v2',
  'discount-matrix': 'Show the discount matrix page',
  'replace-item': 'Show the replace item action in central assortments',
};

const columns: GridColDef<RowType>[] = [
  {
    field: 'name',
    headerName: 'Feature',
    flex: 1,
    renderCell: ({ value }) => <Chip label={value} />,
  },
  {
    field: 'description',
    headerName: 'Description',
    flex: 1,
    renderCell: ({ row }) => descriptionMap[row.name],
  },
  {
    field: 'dev',
    headerName: 'Dev',
    flex: 1,
    renderCell: ({ row }) => {
      const feature = row.envs.dev;
      return <FeatureChip feature={feature} />;
    },
  },
  {
    field: 'stage',
    headerName: 'Stage',
    flex: 1,
    renderCell: ({ row }) => {
      const feature = row.envs.stage;
      return <FeatureChip feature={feature} />;
    },
  },
  {
    field: 'prod',
    headerName: 'Prod',
    flex: 1,
    renderCell: ({ row }) => {
      const feature = row.envs.prod;
      return <FeatureChip feature={feature} />;
    },
  },
];

export const FeatureTogglesTable = () => {
  const { getRowColorForStripedTable } = useRowColorGrouping();

  return (
    <DataGridPremium
      columns={columns}
      rows={features}
      getRowId={(row) => row.name}
      getRowClassName={getRowColorForStripedTable}
      disableColumnMenu
      disableRowSelectionOnClick
      hideFooter
      sx={{ width: '1200px' }}
    />
  );
};

const formatScope = (scope: FeatureScope): string => {
  const parts: string[] = [];
  if (scope.orgUnitType) parts.push(scope.orgUnitType.getName());
  if (scope.country) parts.push(scope.country);
  return parts.length > 0 ? parts.join(' · ') : 'everyone';
};

export const FeatureChip = ({ feature }: { feature: FeatureFlag | undefined }) => {
  const theme = useTheme();
  const { status } = theme.vars.palette.semantic;

  if (!feature?.enabled) {
    return (
      <Chip
        backgroundColor={status.negative.medium}
        textColor={status.negative.text}
        label="Disabled"
      />
    );
  }

  const excludedScopes = feature.excludedScopes ?? [];

  if (excludedScopes.length === 0) {
    return (
      <Chip
        backgroundColor={status.positive.soft}
        textColor={status.positive.text}
        label="Enabled"
      />
    );
  }

  return (
    <Tooltip
      title={
        <Stack>
          <Typography variant="caption">Not shown to:</Typography>
          {excludedScopes.map((scope, index) => (
            <Typography key={index} variant="caption">
              • {formatScope(scope)}
            </Typography>
          ))}
        </Stack>
      }
    >
      <Stack spacing={0.5} alignItems="flex-start">
        <Chip
          backgroundColor={status.pending.strong}
          textColor={status.pending.text}
          label={`Enabled · ${excludedScopes.length} excluded`}
        />
        <Typography variant="caption" color="text.secondary" noWrap>
          {excludedScopes.map(formatScope).join(', ')}
        </Typography>
      </Stack>
    </Tooltip>
  );
};
